import React, { ReactNode } from "react";
import $ from "jquery";
import { renderToString } from "react-dom/server";
import moment from "moment";
import DataTable, { DataTableRef } from "datatables.net-react";
import DT, { Api, Config, ConfigColumns, Feature } from "datatables.net-dt";
import "datatables.net-colreorder-bs5";
import "datatables.net-fixedcolumns-bs5";
import "datatables.net-fixedheader-bs5";
import "datatables.net-rowgroup-bs5";
import "datatables.net-responsive-bs5";
import "datatables.net-select-bs5";
import "datatables.net-rowreorder-bs5";
import "datatables.net-scroller-bs5";
/*import "datatables.net-keytable-bs5"*/
/*import "datatables.net-searchpanes-bs5";*/

import { ControlButton, ControlButtonProps, IControlStyles } from "../.";
import { ControlDataGridGlobalStyle } from "./controlDataGrid.styled";
import { FormatHelper, GuidHelper } from "../../../../utils/helpers";
import { CSSObject } from "styled-components";
import { Constants } from "../../../../utils/lib/constants";
import {
    DataGridSearchModel,
    PagingModel,
    SortDirection,
    SortModel,
} from "../../../../utils/lib";
import { GRID_ACTION_TYPES } from "../../../../types";
import { useSearchParams } from "react-router-dom";

DataTable.use(DT);

// #region Props

export type GridQuery = {
    pageNumber?: number;
    pageSize?: number;
    sortBy?: SortModel;
    totalRecords?: number;
};

export interface ControlDataGridProps extends DataGridSearchModel {
    cols: ControlDataGridColumnProps[];
    data?: any | undefined;
    dataId?: string | undefined;
    height?: string | undefined;
    isActionButtonClick?: boolean | undefined;
    loading?: boolean | undefined;

    behaviorOptions?: ControlDataGridBehaviorProps | undefined;
    layoutOptions?: ControlDataGridLayoutProps | undefined;
    actionCol?: ControlDataGridActionColumnProps;

    onCustomEvent?: (actionType: string, dataId: string) => void;
    onParentRowClick?: (data: any, subTableId: string) => void;
    query?: GridQuery;
    onQueryChange?: (patch: Partial<GridQuery>) => void;
    totalRecords?: number;
    uiOptions?: ControlDataGridUiProps | undefined;
}
export interface ControlDataGridColumnProps {
    data: string | null;
    title: string;
    columnType:
    | "action"
    | "custom"
    | "text"
    | "phone"
    | "ssn"
    | "last4Ssn"
    | "currency"
    | "number"
    | "date"
    | "time"
    | "datetime"
    | "select";
    visible?: boolean | undefined;
    alignment?: "left" | "center" | "right" | undefined;
    className?: string;
    sortable?: boolean | undefined;
    width?: string | undefined;
    customCell?: Function;
}
export interface ControlDataGridBehaviorProps {
    autoWidth?: boolean | undefined;
    colReorder?: boolean | undefined;
    continuousScroll?: boolean | undefined;
    fixedColumns?: ControlDataGridFixedColumnProps | boolean | undefined;
    fixedHeader?: boolean | undefined;
    paging?: boolean | undefined;
    processing?: boolean | undefined;
    responsive?: boolean | undefined;
    rowGroup?: boolean | undefined;
    rowReorder?: boolean | undefined;
    scrollCollapse?: boolean | undefined;
    scrollX?: boolean | undefined;
    selection?: ControlDataGridSelectionProps | boolean | undefined;
}
export interface ControlDataGridLayoutProps {
    displayPageLength?: boolean | undefined;
    displayPageInfo?: boolean | undefined;
    displayPaging?: boolean | undefined;
    pageLengthMenu?: Array<number | { label: string; value: number }>;
}
export interface ControlDataGridSelectionProps {
    selectionMode?: "none" | "multiple" | "single";
    selectionStatic?: boolean | undefined;
    selectionType?: "row" | "column" | "cell";
}
export interface ControlDataGridFixedColumnProps {
    start?: number | undefined;
    end?: number | undefined;
}
export interface ControlDataGridActionColumnProps
    extends ControlDataGridColumnProps {
    controlStyles: IControlStyles;
    actionButtons:
    | ControlDatagridActionButtonProps[]
    | ((row: any) => ControlDatagridActionButtonProps[]);
}
export interface ControlDataGridUiActionProps {
    text: string;
    icon?: string | undefined;
    onClick?: (() => void) | undefined;
}
export interface ControlDataGridUiProps {
    title?: string | undefined;
    subtitle?: string | undefined;
    searchEnabled?: boolean | undefined;
    searchPlaceholder?: string | undefined;
    filterContent?: ReactNode | undefined;
    primaryAction?: ControlDataGridUiActionProps | undefined;
    toolbarActions?: ControlDataGridUiActionProps[] | undefined;
    isChildGrid?: boolean | undefined;
    childTitle?: string | undefined;
    childCount?: number | undefined;
    footerAction?: ControlDataGridUiActionProps | undefined;
}
type RowData = Record<string, unknown>;
type NormalizedPaging = { page0: number; size: number; total: number };
//#endregion

//#region FormatCell
function normalizePaging(
    pagingModel: PagingModel | undefined,
    dataLen: number
): NormalizedPaging {
    const size = pagingModel?.pageSize ?? 10;
    const oneBased = pagingModel?.pageNumber ?? 1; // public API is 1-based
    const total = pagingModel?.totalRecords ?? dataLen; // fallback to local length
    return { page0: Math.max(0, oneBased - 1), size, total };
}

function columnIndexByField(
    cols: ControlDataGridColumnProps[],
    field?: string
): number {
    if (!field) return -1;
    return cols.findIndex((c) => typeof c.data === "string" && c.data === field);
}

function toDirString(d?: SortDirection): "asc" | "desc" {
    return d === SortDirection.Descending ? "desc" : "asc";
}

function GetColConfig(props: ControlDataGridColumnProps): ConfigColumns {
    const col: ConfigColumns = {
        data: props.data,
        title: props.title,
    };

    FormatCol(props, col);
    RenderDateColConfig(props, col);
    RenderNumberColConfig(props, col);
    RenderTextColConfig(props, col);
    RenderSelectColConfig(props, col);
    RenderCustomColConfig(props, col);

    return col;
}

function FormatCustomCell(
    data: any,
    props: ControlDataGridColumnProps,
    row: any
) {
    if (props.customCell) {
        return props.customCell(data, row);
    }
    return null;
}

function FormatActionCell(
    actionButtons: ControlDatagridActionButtonProps[],
    styles: IControlStyles,
    data?: any
) {
    return (
        <div className="grid-action-button-group">
            {actionButtons.map(
                (item: ControlDatagridActionButtonProps, index: number) => (
                    <DatagridActionButton
                        controlStyles={styles}
                        key={"grid_action_button_" + index}
                        {...SetActionButtonProps(item, data)}
                    />
                )
            )}
        </div>
    );
}

const SetActionButtonProps = (
    props: ControlDatagridActionButtonProps,
    data?: any
): ControlDatagridActionButtonConfigurationProps => {
    const target: ControlDatagridActionButtonConfigurationProps = {
        dataId: data,
    };
    const componentProps = Object.assign(target, props);

    return componentProps;
};

function FormatColumns(props: ControlDataGridProps): ConfigColumns[] {
    const columns: ConfigColumns[] = [];

    props.cols.map((item: ControlDataGridColumnProps, index: number) =>
        columns.push(GetColConfig(item))
    );

    var actionCol = RenderActionColConfig(props.actionCol, props.dataId);
    if (actionCol !== null && actionCol !== undefined) {
        columns.push(actionCol);
    }

    return columns;
}

function FormatCol(props: ControlDataGridColumnProps, col: ConfigColumns) {
    if (props.alignment !== undefined) {
        col.className = (props.className ?? "") + " " + (col.className ?? "") + " dt-" + props.alignment;
    }

    if (props.sortable !== undefined) {
        col.orderable = props.sortable;
    }

    if (props.visible !== undefined) {
        col.visible = props.visible;
    }

    if (props.width !== undefined) {
        col.width = props.width;
    }
}
function RenderDateColConfig(
    props: ControlDataGridColumnProps,
    col: ConfigColumns
) {
    const validTypes: string[] = ["date", "time", "datetime"];
    if (!validTypes.includes(props.columnType)) {
        return;
    }

    col.type = "date";

    if (props.columnType === "date") {
        col.render = (data) =>
            data ? moment(data).format("MM/DD/YYYY") : Constants.emptyString;
    }

    if (props.columnType === "time") {
        col.render = (data) => moment(data).format("hh:mm A");
    }

    if (props.columnType === "datetime") {
        col.render = (data) => moment(data).format("MM/DD/YYYY hh:mm A");
    }
}
function RenderSelectColConfig(
    props: ControlDataGridColumnProps,
    col: ConfigColumns
) {
    const validTypes: string[] = ["select"];
    if (!validTypes.includes(props.columnType)) {
        return;
    }

    if (props.columnType === "select") {
        col.render = DT.render.select();
    }
}
function RenderNumberColConfig(
    props: ControlDataGridColumnProps,
    col: ConfigColumns
) {
    const validTypes: string[] = ["number", "currency"];
    if (!validTypes.includes(props.columnType)) {
        return;
    }

    col.type = "num-fmt";

    if (props.columnType === "number") {
        col.render = DT.render.number();
    }

    if (props.columnType === "currency") {
        col.render = DT.render.number(",", ".", 2, "$");
    }
}
function RenderTextColConfig(
    props: ControlDataGridColumnProps,
    col: ConfigColumns
) {
    const validTypes: string[] = ["text", "phone", "ssn", "last4Ssn"];
    if (!validTypes.includes(props.columnType)) {
        return;
    }

    col.type = "text";

    if (props.columnType === "text") {
        col.render = DT.render.text();
    }

    if (props.columnType === "phone") {
        col.render = (data) => FormatHelper.formatPhone(data);
    }

    if (props.columnType === "ssn") {
        col.render = (data) => FormatHelper.formatSSN(data);
    }

    if (props.columnType === "last4Ssn") {
        col.render = (data) => FormatHelper.formatLastFourSSN(data);
    }
}
function RenderActionColConfig(
    props: ControlDataGridActionColumnProps | undefined,
    dataId: string | undefined
): ConfigColumns | undefined {
    if (props === null || props === undefined || dataId === undefined) {
        return;
    }

    const col: ConfigColumns = {
        data: dataId,
        title: props.title ?? "Actions",
        orderable: props.sortable ?? false,
    };

    FormatCol(props, col);

    col.render = (data, type, row) => {
        const actionButtons = FilterActionButtons(props.actionButtons, row);

        if (!actionButtons || actionButtons.length === 0) return "";

        return renderToString(
            FormatActionCell(actionButtons, props.controlStyles, data)
        );
    };

    return col;
}
function RenderCustomColConfig(
    props: ControlDataGridColumnProps,
    col: ConfigColumns
) {
    const validTypes: string[] = ["custom"];

    if (
        !validTypes.includes(props.columnType) ||
        props.customCell === undefined
    ) {
        return;
    }

    col.render = (data, type, row) => {
        return renderToString(FormatCustomCell(data, props, row));
    };
}

function FilterActionButtons(
    actionButtons:
        | ControlDatagridActionButtonProps[]
        | ((row: any) => ControlDatagridActionButtonProps[]),
    row: any
): ControlDatagridActionButtonProps[] {
    let buttons: ControlDatagridActionButtonProps[] = [];

    if (typeof actionButtons === "function") {
        buttons = actionButtons(row);
    } else if (Array.isArray(actionButtons)) {
        buttons = actionButtons;
    }

    return buttons.filter(
        (item) => item && item.eventType !== null && item.eventType !== undefined
    );
}

function GetRowDoubleClickAction(
    props: ControlDataGridProps,
    rowData: RowData | undefined
): string {
    if (!rowData || !props.actionCol?.actionButtons) {
        return GRID_ACTION_TYPES.DOUBLE_CLICK;
    }

    const rowActions = FilterActionButtons(props.actionCol.actionButtons, rowData);
    const hasEditAction = rowActions.some(
        (action) => action.eventType === GRID_ACTION_TYPES.EDIT
    );

    return hasEditAction ? GRID_ACTION_TYPES.EDIT : GRID_ACTION_TYPES.DOUBLE_CLICK;
}

function SetBehaviorOptions(
    props: ControlDataGridProps | undefined,
    options: Config
) {
    options.autoWidth = props?.behaviorOptions?.autoWidth ?? false;
    options.colReorder = props?.behaviorOptions?.colReorder ?? false;
    options.fixedColumns = props?.behaviorOptions?.fixedColumns ?? true;
    options.fixedHeader = props?.behaviorOptions?.fixedHeader ?? true;
    options.paging = props?.behaviorOptions?.paging ?? true;
    options.processing = props?.behaviorOptions?.processing ?? true;
    options.responsive = props?.behaviorOptions?.responsive ?? true;
    options.rowGroup = props?.behaviorOptions?.rowGroup ?? false;
    options.rowReorder = props?.behaviorOptions?.rowReorder ?? false;
    options.scrollCollapse = props?.behaviorOptions?.scrollCollapse ?? true;
    options.scrollX = props?.behaviorOptions?.scrollX ?? true;
    options.scroller = props?.behaviorOptions?.continuousScroll ?? false;
    SetFixedColumnOptions(props?.behaviorOptions?.fixedColumns, options);
    SetSelectionOptions(props?.behaviorOptions?.selection, options);
}
function SetLayoutOptions(
    props: ControlDataGridProps | undefined,
    options: Config
) {
    let pageLength: Feature = {
        pageLength: {
            menu: props?.layoutOptions?.pageLengthMenu ?? [
                10,
                25,
                50,
                100,
                { label: "All", value: -1 },
            ],
            text: "_MENU_ Rows per page",
        },
    };

    let pageInfo: Feature = {
        info: {
            empty: "No Records Found",
            text: "_START_ - _END_ of _TOTAL_ ",
        },
    };

    let paging: Feature = {
        paging: {
            type: "first_last_numbers",
        },
    };

    let bottomLayout: Feature[] = [];
    if (props?.layoutOptions?.displayPageLength ?? true) {
        bottomLayout.push(pageLength);
    }

    if (props?.layoutOptions?.displayPageInfo ?? true) {
        bottomLayout.push(pageInfo);
    }

    if (props?.layoutOptions?.displayPaging ?? true) {
        bottomLayout.push(paging);
    }

    options.layout = {
        topStart: null,
        topEnd: null,
        top: null,
        bottomStart: null,
        bottomEnd: null,
        bottom: bottomLayout,
    };
}
function SetSelectionOptions(
    props: ControlDataGridSelectionProps | boolean | undefined,
    options: Config
) {
    if (props === undefined || props === true) {
        options.select = false;
        return;
    }

    if (props === false) {
        options.select = false;
        return;
    }

    const selectProps = props as ControlDataGridSelectionProps;

    options.select = {
        items: selectProps.selectionType ?? "row",
        blurable: selectProps.selectionStatic ?? false,
        selector: "td:first-child",
        style:
            selectProps.selectionMode === "none"
                ? undefined
                : selectProps.selectionMode === "single"
                    ? "single"
                    : selectProps.selectionMode === "multiple"
                        ? selectProps.selectionType === "column"
                            ? "multi"
                            : "multi+shift"
                        : "single",
    };
}
function SetFixedColumnOptions(
    props: ControlDataGridFixedColumnProps | boolean | undefined,
    options: Config
) {
    if (props === undefined || props === true) {
        options.fixedColumns = true;
        return;
    }

    if (props === false) {
        options.fixedColumns = false;
        return;
    }

    const fixedColProps = props as ControlDataGridFixedColumnProps;

    options.fixedColumns = {
        start: fixedColProps.start,
    };
}

export function FormatNumberColConfig(
    props: ControlDataGridColumnProps,
    col: ConfigColumns
) {
    const validTypes: string[] = ["number", "currency"];
    if (!validTypes.includes(props.columnType)) {
        return;
    }

    col.type = "num-fmt";

    if (props.columnType === "number") {
        col.render = DT.render.number();
    }

    if (props.columnType === "currency") {
        col.render = DT.render.number(",", ".", 2, "$");
    }
}
export function FormatTextColConfig(
    props: ControlDataGridColumnProps,
    col: ConfigColumns
) {
    const validTypes: string[] = ["text", "phone", "ssn", "last4Ssn"];
    if (!validTypes.includes(props.columnType)) {
        return;
    }

    col.type = "text";

    if (props.columnType === "text") {
        col.render = DT.render.text();
    }

    if (props.columnType === "phone") {
        col.render = (data) => FormatHelper.formatPhone(data);
    }

    if (props.columnType === "ssn") {
        col.render = (data) => FormatHelper.formatSSN(data);
    }

    if (props.columnType === "last4Ssn") {
        col.render = (data) => FormatHelper.formatLastFourSSN(data);
    }
}
export function FormatSelectColConfig(
    props: ControlDataGridColumnProps,
    col: ConfigColumns
) {
    const validTypes: string[] = ["select"];
    if (!validTypes.includes(props.columnType)) {
        return;
    }

    if (props.columnType === "select") {
        col.render = DT.render.select();
    }
}

//#endregion

//#region actionButtons

export interface ControlDatagridActionButtonProps extends ControlButtonProps {
    text?: string | undefined;
    display_type?:
    | "text-only"
    | "image-only"
    | "image-before-text"
    | "image-after-text"
    | undefined;
    button_class?: string | undefined;
    image?: string | undefined;
    image_style?: CSSObject | undefined;
    eventType?: string | undefined;
}

interface ControlDatagridActionButtonConfigurationProps
    extends ControlDatagridActionButtonProps {
    dataId?: string | undefined;
}

function formatButton(props: ControlDatagridActionButtonProps): ReactNode {
    if (props.display_type === "image-only" || props.text === undefined) {
        return <i className={props.image} style={props.image_style}></i>;
    }

    if (props.display_type === "image-before-text") {
        return (
            <span>
                <i className={props.image + " me-3"} style={props.image_style}></i>
                {props.text}
            </span>
        );
    }

    if (props.display_type === "image-after-text") {
        return (
            <span>
                {props.text}
                <i className={props.image + " ms-3"} style={props.image_style}></i>
            </span>
        );
    }

    return <span> {props.text} </span>;
}

//#endregion

export const ControlDataGrid: React.FC<ControlDataGridProps> = (props) => {
    const [, setSearchParams] = useSearchParams();
    const isRemote = !!props.onQueryChange;
    const [uiSearchText, setUiSearchText] = React.useState("");
    const isChildGrid = props.uiOptions?.isChildGrid ?? false;
    const searchEnabled = props.uiOptions?.searchEnabled ?? false;
    const filteredData = React.useMemo<RowData[]>(() => {
        const rows = (props.data ?? []) as RowData[];
        const normalizedSearch = uiSearchText.trim().toLowerCase();

        if (!searchEnabled || normalizedSearch.length === 0) {
            return rows;
        }

        return rows.filter((row) =>
            Object.values(row).some((value) =>
                String(value ?? "").toLowerCase().includes(normalizedSearch)
            )
        );
    }, [props.data, searchEnabled, uiSearchText]);
    const hasUiSearch = searchEnabled && uiSearchText.trim().length > 0;
    const dataLen = filteredData.length;
    const totalRecords = hasUiSearch ? filteredData.length : props.totalRecords;
    const pm = React.useMemo(
        () =>
            normalizePaging(
                {
                    pageNumber: props.query?.pageNumber,
                    pageSize: props.query?.pageSize,
                    totalRecords,
                } as PagingModel,
                dataLen
            ),
        [
            props.query?.pageNumber,
            props.query?.pageSize,
            totalRecords,
            dataLen,
        ]
    );

    // Track the last page/size we set programmatically so forward() can ignore those events
    const programmaticRef = React.useRef({ page0: -1, size: -1 });
    const wireActionButtons = React.useCallback(
        (api?: Api<RowData>) => {
            if (!api) return;

            // namespaced unbind & bind
            api.off("click.vfxActions", "[data-grid-action]");
            api.on(
                "click.vfxActions",
                "[data-grid-action]",
                function (this: HTMLElement, e: Event) {
                    e.stopPropagation();

                    const $el = $(this) as JQuery<HTMLElement>;
                    const actionType = $el.data("grid-action") as string | undefined;
                    const dataId = $el.data("grid-key") as string | number | undefined;

                    if (props.onCustomEvent && actionType != null && dataId != null) {
                        props.onCustomEvent(actionType, String(dataId));
                    }
                }
            );
        },
        [props.onCustomEvent]
    );

    const table = React.useRef<DataTableRef>(null);
    const latestPayloadRef = React.useRef<{ rows: RowData[]; total: number }>({
        rows: filteredData,
        total: pm.total,
    });

    React.useEffect(() => {
        latestPayloadRef.current = {
            rows: filteredData,
            total: totalRecords ?? filteredData.length,
        };
    }, [filteredData, totalRecords]);

    React.useEffect(() => {
        const api = table.current?.dt();
        if (!api) return;

        // Unbind old handlers (namespaced so they don't affect other listeners)
        api.off("dblclick.vfxWire", "tr");
        api.off("click.vfxWire", "button.expand");
        api.off("draw.dt.vfxWire");
        api.off("length.dt.vfxWire");
        api.off("click.vfxWire", "button.grid-action-button");

        // Row dblclick
        api.on("dblclick.vfxWire", "tbody > tr:not(.child)", function (e: any) {
            // If the dblclick started inside a nested table, ignore it
            const clickedTable = $(e?.target).closest("table").get(0);
            if (clickedTable && clickedTable !== api.table().node()) return;
            const rowData = api.row(this).data();
            onClick(GetRowDoubleClickAction(props, rowData), rowData);
        });
        // Prevent nested/sub-table dblclicks from bubbling to the parent grid
        api.on("dblclick.vfxWire", ".sub-table-container tr", function (e: any) {
            e.stopPropagation();
        });

        // Expand/collapse
        api.on("click.vfxWire", "button.expand", handleExpandClick(api));

        // Rewire action buttons on draw/length changes (namespaced)
        api.on("draw.dt.vfxWire", () => wireActionButtons(api));
        api.on("length.dt.vfxWire", () => wireActionButtons(api));

        // Optional custom action buttons
        if (props.isActionButtonClick) {
            api.on(
                "click.vfxWire",
                "button.grid-action-button",
                function (this: HTMLElement) {
                    onCustomEvent(
                        this.getAttribute("data-grid-action") ?? "",
                        this.getAttribute("data-grid-key") ?? ""
                    );
                }
            );
        }

        // Initial wire
        wireActionButtons(api);

        return () => {
            api.off("dblclick.vfxWire", "tr");
            api.off("click.vfxWire", "button.expand");
            api.off("draw.dt.vfxWire");
            api.off("length.dt.vfxWire");
            api.off("click.vfxWire", "button.grid-action-button");
            api.off(".vfxActions");
        };
    }, [props, props.isActionButtonClick, props.onParentRowClick, wireActionButtons]);

    React.useEffect(() => {
        const api = table.current?.dt();
        if (!api) return;

        // Page-button clicks: only forward the new page number
        const onPageChange = () => {
            const info = api.page.info();
            const { page0: lastP, size: lastS } = programmaticRef.current;

            // Ignore echoes of our own programmatic api.page() calls
            if (info.page === lastP && info.length === lastS) return;

            props.onQueryChange?.({
                pageNumber: info.page + 1,
                pageSize: info.length,
            });
        };

        // Rows-per-page dropdown: only forward the new size, KEEP the current page
        // DataTables internally resets to page 0 on length change, so info.page would
        // always be 0 here — we must ignore it and let the parent keep its page number.
        const onLengthChange = () => {
            const info = api.page.info();
            const { page0: lastP, size: lastS } = programmaticRef.current;

            // Ignore echoes of our own programmatic api.page.len() calls
            if (info.page === lastP && info.length === lastS) return;

            props.onQueryChange?.({
                pageNumber: 1,   // reset to first page when page size changes
                pageSize: info.length,
            });
        };

        api.off("page.dt.vfx length.dt.vfx");
        api.on("page.dt.vfx", onPageChange);
        api.on("length.dt.vfx", onLengthChange);

        return () => {
            api.off("page.dt.vfx length.dt.vfx");
        };
    }, [props.onQueryChange]);

    React.useEffect(() => {
        const api = table.current?.dt();
        if (!api) return;

        api.off("order.dt.vfxsort");
        api.on("order.dt.vfxsort", () => {
            const order = api.order() as [number, "asc" | "desc"][];
            const [colIndex, dir] = order?.[0] ?? [];
            if (typeof colIndex !== "number" || !dir) return;
            if (!api.column(colIndex).orderable()) return;

            const field =
                typeof api.column(colIndex).dataSrc() === "string"
                    ? (api.column(colIndex).dataSrc() as string)
                    : undefined;
            if (!field) return;

            const next: SortModel = {
                field,
                direction:
                    dir === "desc" ? SortDirection.Descending : SortDirection.Ascending,
            };

            // avoid loops
            const prev = props.query?.sortBy;
            if (prev?.field === next.field && prev?.direction === next.direction)
                return;

            // snap to page 1 on sort change
            props.onQueryChange?.({ sortBy: next, pageNumber: 1 });
        });

        return () => {
            api.off("order.dt.vfxsort");
        };
    }, [props.onQueryChange, props.query?.sortBy]);

    React.useEffect(() => {
        const api = table.current?.dt();
        if (!api) return;

        // Record what we're about to set so forward() can identify these as programmatic
        programmaticRef.current = { page0: pm.page0, size: pm.size };

        const info = api.page.info();
        let changed = false;
        if (info.length !== pm.size) { api.page.len(pm.size); changed = true; }
        if (info.page !== pm.page0) { api.page(pm.page0); changed = true; }
        if (changed) api.draw("page");
    }, [pm.page0, pm.size]);

    React.useEffect(() => {
        const api = table.current?.dt() as Api<RowData> | undefined;
        if (!api) return;

        const processableApi = api as Api<RowData> & {
            processing?: (show: boolean) => void;
        };
        if (processableApi.processing) {
            processableApi.processing(Boolean(props.loading));
            return;
        }

        $(api.table().container())
            .find("div.dt-processing")
            .css("display", props.loading ? "block" : "none");
    }, [props.loading]);

    React.useEffect(() => {
        const api = table.current?.dt() as Api<RowData> | undefined;
        if (!api) return;

        if (isRemote) {
            // works because GetTableOptions() provided an ajax function in remote mode
            api.ajax.reload(undefined, false); // keep pager position
        } else {
            // client-side fallback: replace rows and redraw
            api.clear();
            if (filteredData.length) api.rows.add(filteredData);
            api.draw(false);
        }
    }, [isRemote, filteredData, pm.total]);

    const onCustomEvent = (actionType: string, dataId: string) => {
        if (props.onCustomEvent && actionType && dataId !== null) {
            props.onCustomEvent(actionType, dataId);
        }
    };

    function GetTableOptions(p: ControlDataGridProps): Config {
        const options: Config = { scrollY: p.height ?? "55vh" };

        if (isRemote) {
            const pageNumber = p.query?.pageNumber ?? 1;
            const pageSize = p.query?.pageSize ?? 10;

            options.pageLength = pageSize;
            options.displayStart = (pageNumber - 1) * pageSize;

            const idx = columnIndexByField(p.cols, p.query?.sortBy?.field);
            if (idx >= 0) {
                const dir: "asc" | "desc" = toDirString(p.query?.sortBy?.direction);
                options.order = [[idx, dir]];
            }

            options.serverSide = true;
            options.processing = p?.behaviorOptions?.processing ?? true;
            options.searching = false;

            // --- Strongly-typed ajax function ---
            type DtRequest = { draw: number };
            type DtResponse<T = unknown> = {
                draw: number;
                data: T[];
                recordsTotal: number;
                recordsFiltered: number;
            };

            // Config['ajax'] is a union (string | object | function). We provide the function variant:
            type AjaxFn = Extract<Config["ajax"], Function>;
            const ajaxImpl = ((
                request: DtRequest,
                callback: (resp: DtResponse) => void
            ) => {
                const snap = latestPayloadRef.current;
                callback({
                    draw: request.draw,
                    data: snap.rows,
                    recordsTotal: snap.total,
                    recordsFiltered: snap.total,
                });
            }) as unknown as AjaxFn;

            options.ajax = ajaxImpl;
        }

        SetBehaviorOptions(p, options);
        SetLayoutOptions(p, options);
        return options;
    }

    const handleExpandClick = (api: Api<RowData>) =>
        async function (this: HTMLElement) {
            const uid = GuidHelper.generateGuid();
            const tr = this.closest("tr");
            if (!tr) return;

            const row = api.row(tr);
            const rowData = row.data();

            const toggleRow = (isShown: boolean) => {
                if (isShown) {
                    row.child.hide();
                    tr.classList.remove("shown");
                    this.classList.add("bi-plus-lg");
                    this.classList.remove("bi-dash-lg");
                    this.setAttribute("aria-expanded", "false");
                } else {
                    const subTableId = `sub-table-${uid}`;
                    const subTable = `<div id="${subTableId}" class="sub-table-container"></div>`;
                    row.child(subTable).show();
                    tr.classList.add("shown");
                    this.classList.remove("bi-plus-lg");
                    this.classList.add("bi-dash-lg");
                    this.setAttribute("aria-expanded", "true");
                    if (props.onParentRowClick) {
                        props.onParentRowClick(rowData, subTableId);
                    }
                }
            };

            if (rowData) {
                // Check if the child row is already shown
                const isChildRowShown = row.child.isShown();
                toggleRow(isChildRowShown);

                // Call the onParentRowClick with null if collapsing
                if (isChildRowShown && props.onParentRowClick) {
                    props.onParentRowClick(rowData, `sub-table-${uid}`);
                }
            }
        };

    function onClick(actionType: string, data: any | undefined) {
        if (!data || !props || !props.dataId) {
            return;
        }

        const dataId = data[props.dataId];
        onCustomEvent(actionType, dataId);
    }

    const formattedColumns = React.useMemo(
        () => FormatColumns(props),
        [props.cols, props.actionCol, props.dataId]
    );

    const gridKey = React.useMemo(
        () =>
            `${isRemote ? "remote" : "local"}::${JSON.stringify(formattedColumns)}`,
        [isRemote, formattedColumns]
    );

    const renderUiAction = (
        action: ControlDataGridUiActionProps,
        className: string
    ): React.JSX.Element => (
        <button
            className={className}
            key={`${action.text}-${action.icon ?? ""}`}
            onClick={action.onClick}
            type="button"
        >
            {action.icon && <i className={action.icon} />}
            {action.text}
        </button>
    );

    const renderResourceHeader = (): React.JSX.Element | null => {
        if (
            isChildGrid ||
            (!props.uiOptions?.title &&
                !props.uiOptions?.subtitle &&
                !props.uiOptions?.primaryAction)
        ) {
            return null;
        }

        return (
            <div className="vfx-datagrid-resource-header">
                <div>
                    {props.uiOptions?.title && (
                        <h2 className="vfx-datagrid-resource-title">
                            {props.uiOptions.title}
                        </h2>
                    )}
                    {props.uiOptions?.subtitle && (
                        <p className="vfx-datagrid-resource-subtitle">
                            {props.uiOptions.subtitle}
                        </p>
                    )}
                </div>
                {props.uiOptions?.primaryAction &&
                    renderUiAction(
                        props.uiOptions.primaryAction,
                        "btn vfx-datagrid-resource-primary-action"
                    )}
            </div>
        );
    };

    const renderResourceToolbar = (): React.JSX.Element | null => {
        const hasFilterContent = !!props.uiOptions?.filterContent;
        const hasToolbarActions = (props.uiOptions?.toolbarActions ?? []).length > 0;

        if (
            isChildGrid ||
            (!searchEnabled && !hasFilterContent && !hasToolbarActions)
        ) {
            return null;
        }

        return (
            <div className="vfx-datagrid-resource-toolbar">
                {searchEnabled && (
                    <div className="vfx-datagrid-resource-search">
                        <input
                            aria-label="Search grid"
                            className="form-control"
                            onChange={(event) => setUiSearchText(event.target.value)}
                            placeholder={
                                props.uiOptions?.searchPlaceholder ?? "Search resources..."
                            }
                            type="search"
                            value={uiSearchText}
                        />
                        <i className="bi bi-search" />
                    </div>
                )}
                {hasFilterContent && (
                    <div className="vfx-datagrid-resource-filters">
                        {props.uiOptions?.filterContent}
                    </div>
                )}
                {(props.uiOptions?.toolbarActions ?? []).map((action) =>
                    renderUiAction(action, "btn vfx-datagrid-resource-toolbar-action")
                )}
            </div>
        );
    };

    const renderChildHeader = (): React.JSX.Element | null => {
        if (!isChildGrid) return null;

        return (
            <div className="vfx-datagrid-resource-child-header">
                <span />
                <strong>
                    {props.uiOptions?.childTitle ?? "Child Resources"} (
                    {props.uiOptions?.childCount ?? pm.total})
                </strong>
            </div>
        );
    };

    const renderFooterAction = (): React.JSX.Element | null => {
        if (!props.uiOptions?.footerAction) return null;

        return (
            <div className="vfx-datagrid-resource-footer-action">
                {renderUiAction(
                    props.uiOptions.footerAction,
                    "btn vfx-datagrid-resource-link-action"
                )}
            </div>
        );
    };

    const dataTable = (
        <DataTable
            key={gridKey}
            className="table display vfx-datagrid-resource-table"
            ref={table}
            data={filteredData}
            columns={formattedColumns}
            options={GetTableOptions(props)}
        />
    );

    return (
        <>
            <ControlDataGridGlobalStyle />
            <div
                className={`vfx-datagrid-resource ${isChildGrid ? "vfx-datagrid-resource-child" : ""
                    }`}
            >
                {renderResourceHeader()}
                <div className="vfx-datagrid-resource-card">
                    {renderChildHeader()}
                    {renderResourceToolbar()}
                    {dataTable}
                    {renderFooterAction()}
                </div>
            </div>
        </>
    );
};

const ActionComponentProps = (
    props: ControlDatagridActionButtonConfigurationProps
): ControlButtonProps => {
    const componentProps = Object.assign({}, props);
    delete componentProps.text;
    delete componentProps.display_type;
    delete componentProps.button_class;
    delete componentProps.image;
    delete componentProps.image_style;
    delete componentProps.eventType;
    delete componentProps.dataId;

    return componentProps;
};

export const DatagridActionButton: React.FC<
    ControlDatagridActionButtonConfigurationProps
> = (props) => {
    const buttonClass =
        "grid-action-button p-0 mx-2 btn " + (props.button_class ?? "");
    return (
        <ControlButton
            {...ActionComponentProps(props)}
            data-grid-action={props.eventType}
            data-grid-key={props?.dataId}
            className={buttonClass}
        >
            {formatButton(props)}
        </ControlButton>
    );
};